import {useState} from 'react';
import {CheckCircle2, Download, RotateCcw, PackagePlus} from 'lucide-react';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {DataTable} from '@common-web/components/data-table';
import type {ImportResult} from './types';
import {AssignProductsPanel} from './AssignProductsPanel';

type ResultsStepProps = {
  importResult: ImportResult;
  groupId: string;
  onDownloadErrors: () => void;
  onReset: () => void;
  urlGroupId: string | undefined;
  onNavigateToMembers: () => void;
};

export function ResultsStep({
  importResult,
  groupId,
  onDownloadErrors,
  onReset,
  urlGroupId,
  onNavigateToMembers,
}: ResultsStepProps) {
  const [assignOpen, setAssignOpen] = useState(false);
  const canAssignProducts = !!groupId && importResult.createdMembers.length > 0;

  return (
    <div className='space-y-6'>
      <Card className='border-0 shadow-lg bg-white overflow-hidden'>
        <CardHeader className='bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-6 py-4'>
          <CardTitle className='text-lg font-semibold text-brand-navy'>
            Import Results
          </CardTitle>
        </CardHeader>
        <CardContent className='p-6'>
          <div className='grid grid-cols-1 md:grid-cols-4 gap-4 mb-6'>
            <div className='p-4 rounded-lg bg-gray-50 text-center'>
              <p className='text-2xl font-bold text-brand-navy'>{importResult.totalRows}</p>
              <p className='text-xs text-gray-500'>Total Rows</p>
            </div>
            <div className='p-4 rounded-lg bg-emerald-50 text-center'>
              <p className='text-2xl font-bold text-emerald-600'>{importResult.created}</p>
              <p className='text-xs text-gray-500'>Created</p>
            </div>
            <div
              className={`p-4 rounded-lg text-center ${
                importResult.errors.length > 0 ? 'bg-red-50' : 'bg-gray-50'
              }`}
            >
              <p
                className={`text-2xl font-bold ${
                  importResult.errors.length > 0 ? 'text-red-600' : 'text-gray-600'
                }`}
              >
                {importResult.errors.length}
              </p>
              <p className='text-xs text-gray-500'>Errors</p>
            </div>
            <div className='p-4 rounded-lg bg-gray-50 text-center'>
              <p className='text-2xl font-bold text-amber-600'>
                {importResult.warnings.length}
              </p>
              <p className='text-xs text-gray-500'>Warnings</p>
            </div>
          </div>

          {importResult.errors.length === 0 && (
            <div className='flex items-center gap-2 p-3 rounded-lg bg-emerald-50 mb-4'>
              <CheckCircle2 className='h-5 w-5 text-emerald-600' />
              <span className='text-sm text-emerald-700 font-medium'>
                Import completed successfully.
              </span>
            </div>
          )}

          <div className='flex items-center gap-3'>
            {importResult.errors.length > 0 && (
              <Button variant='outline' onClick={onDownloadErrors}>
                <Download className='h-4 w-4 mr-2' />
                Download Error CSV
              </Button>
            )}
            <Button variant='outline' onClick={onReset}>
              <RotateCcw className='h-4 w-4 mr-2' />
              Import Another File
            </Button>
            {canAssignProducts && (
              <Button variant='outline' onClick={() => setAssignOpen(true)}>
                <PackagePlus className='h-4 w-4 mr-2' />
                Assign Products
              </Button>
            )}
            <Button onClick={onNavigateToMembers}>
              {urlGroupId ? 'View Members' : 'Back to Search'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {canAssignProducts && (
        <AssignProductsPanel
          groupId={groupId}
          members={importResult.createdMembers}
          open={assignOpen}
          onOpenChange={setAssignOpen}
        />
      )}

      {importResult.errors.length > 0 && (
        <Card className='border-0 shadow-lg bg-white overflow-hidden'>
          <CardHeader className='bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-6 py-4'>
            <CardTitle className='text-lg font-semibold text-red-700'>
              Error Details ({importResult.errors.length})
            </CardTitle>
          </CardHeader>
          <CardContent className='p-0'>
            <DataTable<{row: number; message: string}>
              columns={[
                {
                  id: 'row',
                  header: 'Row',
                  render: (item) => <span className='font-mono text-sm'>{item.row}</span>,
                },
                {
                  id: 'message',
                  header: 'Error',
                  render: (item) => <span className='text-sm text-red-700'>{item.message}</span>,
                },
              ]}
              data={importResult.errors}
              loading={false}
              emptyMessage='No errors'
            />
          </CardContent>
        </Card>
      )}

      {importResult.warnings.length > 0 && (
        <Card className='border-0 shadow-lg bg-white overflow-hidden'>
          <CardHeader className='bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-6 py-4'>
            <CardTitle className='text-lg font-semibold text-amber-700'>
              Warnings ({importResult.warnings.length})
            </CardTitle>
          </CardHeader>
          <CardContent className='p-0'>
            <DataTable<{row: number; message: string}>
              columns={[
                {
                  id: 'row',
                  header: 'Row',
                  render: (item) => <span className='font-mono text-sm'>{item.row}</span>,
                },
                {
                  id: 'message',
                  header: 'Warning',
                  render: (item) => <span className='text-sm text-amber-700'>{item.message}</span>,
                },
              ]}
              data={importResult.warnings}
              loading={false}
              emptyMessage='No warnings'
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
